import imageCompression from "browser-image-compression";
import { formatBytes } from "./formatBytes";
import { addWatermarkToPhoto } from "./watermarkImage";
import { getBrowserInfo } from "./browserInfo";
import { trackGoal } from "./trackGoal";
import { logPhotoProcessingEvent, PhotoProcessingEventType } from "./logSigningEvent";
import { setupPdfWorker } from "../utils/pdfWorkerSetup";
import { httpClient } from "../funcs";

// Обёртка готового изображения в ту же разметку, что отдаёт getBase64,
// чтобы серверный фолбэк давал идентичный результат отображения.
export function wrapPhotoElement(image: string, setImageStringTag: boolean): string {
    if (!setImageStringTag) {
        return image;
    }
    return `<div style='max-width: 80%; margin-left: auto; margin-right: auto; display: block; margin-bottom: 15px; page-break-inside: avoid;'>
                                 <img style='max-width: 100%; max-height: 450px; width: auto; height: auto; margin-left: auto; margin-right: auto; display: block; page-break-inside: avoid; border-radius: 10px' src='${image}' alt="Фото"/>
                               </div>`;
}

/**
 * Серверный фолбэк обработки фото. Используется, когда клиентская обработка
 * (canvas/HEIC/видео/PDF) упала: отправляем оригинал в микросервис, который
 * чинит ориентацию, сжимает и накладывает водяной знак, и возвращает data URL.
 * Возвращает результат в том же формате, что и getBase64.
 */
export async function processFileViaServer(
    file: File,
    maxWidthOrHeight: number = 1024,
    setImageStringTag: boolean = true,
    addWatermark: boolean = true,
): Promise<string> {
    const formData = new FormData();
    formData.append("file", file, file.name || "upload");
    formData.append("max_width_or_height", String(maxWidthOrHeight));
    formData.append("add_watermark", addWatermark === false ? "false" : "true");

    // Общий секрет для микросервиса (защита от использования посторонними).
    const photoToken = process.env.REACT_APP_PHOTO_SERVICE_TOKEN;
    const headers: Record<string, string> = {};
    if (photoToken) {
        headers["X-Photo-Token"] = photoToken;
    }

    // Content-Type с boundary axios/браузер проставят сами для FormData.
    const response = await httpClient.post("/photo/process", formData, {
        timeout: 60000,
        headers,
    });

    const dataUrl: unknown = response?.data?.image;
    if (typeof dataUrl !== "string" || !dataUrl.startsWith("data:image")) {
        throw new Error("Сервис обработки фото вернул пустой результат");
    }

    return wrapPhotoElement(dataUrl, setImageStringTag);
}

// Функция для конвертации PDF в изображение
export async function convertPdfToImage(file: File): Promise<File> {
    try {
        console.log('Начало загрузки PDF файла');
        const arrayBuffer = await file.arrayBuffer();
        
        // Проверяем поддержку Array.prototype.at() и добавляем полифил если нужно
        if (!Array.prototype.at) {
            Array.prototype.at = function(n) {
                n = Math.trunc(n) || 0;
                if (n < 0) n += this.length;
                if (n < 0 || n >= this.length) return undefined;
                return this[n];
            };
        }
        
        // Динамически импортируем pdfjs-dist только когда он нужен
        const pdfjsLib = await import('pdfjs-dist');
        setupPdfWorker(pdfjsLib);

        console.log('Создание PDF документа');
        const pdf = await pdfjsLib.getDocument({ 
            data: arrayBuffer,
            verbosity: 0  // Уменьшаем вывод отладочной информации
        }).promise;
        
        console.log('Получение первой страницы');
        const page = await pdf.getPage(1);
        
        // Устанавливаем масштаб для хорошего качества
        const scale = 1.0;
        const viewport = page.getViewport({ scale });
        
        console.log('Создание canvas для рендеринга');
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        
        if (!context) {
            throw new Error('Не удалось получить 2D контекст canvas');
        }
        
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        
        console.log('Рендеринг PDF на canvas');
        await page.render({
            canvasContext: context,
            viewport: viewport
        }).promise;
        
        console.log('Конвертация canvas в blob');
        const blob = await new Promise<Blob>((resolve, reject) => {
            try {
                canvas.toBlob(
                    (blob: any) => {
                        if (blob) {
                            resolve(blob);
                        } else {
                            reject(new Error('Не удалось создать blob из canvas'));
                        }
                    },
                    'image/jpeg',
                    0.95
                );
            } catch (error) {
                reject(error);
            }
        });
        
        console.log('Создание нового файла');
        const convertedFile = new File([blob], file.name.replace(/\.pdf$/i, '.jpg'), {
            type: 'image/jpeg',
            lastModified: file.lastModified
        });
        
        console.log('PDF успешно конвертирован в изображение');
        return convertedFile;
    } catch (error) {
        console.error('Детальная ошибка при конвертации PDF:', error);
        
        // Определяем тип ошибки и формируем понятное сообщение
        let errorMessage = 'Не удалось обработать PDF файл.';
        
        if (error instanceof Error) {
            if (error.message.includes('at is not a function') || 
                error.message.includes('Array.prototype.at') ||
                error.message.includes('KeyboardManager')) {
                
                errorMessage = 'Конвертация PDF не поддерживается в вашем браузере. Пожалуйста:\n' +
                              '1. Сделайте скриншот PDF документа\n' +
                              '2. Или конвертируйте PDF в JPG на компьютере\n' +
                              '3. Или загрузите изображение напрямую';
            } else if (error.message.includes('Failed to fetch')) {
                errorMessage = 'Не удалось загрузить PDF файл. Проверьте:\n' +
                              '1. Размер файла (не более 10MB)\n' +
                              '2. Формат файла (должен быть PDF)\n' +
                              '3. Соединение с интернетом';
            } else if (error.message.includes('Invalid PDF')) {
                errorMessage = 'Файл поврежден или не является PDF документом. Пожалуйста:\n' +
                              '1. Проверьте, что файл не поврежден\n' +
                              '2. Убедитесь, что это PDF документ\n' +
                              '3. Попробуйте другой файл';
            } else {
                errorMessage = `Ошибка при обработке PDF: ${error.message}`;
            }
        }
        
        throw new Error(errorMessage);
    }
}

// Функция для конвертации HEIC в JPEG
export async function convertHeicToJpeg(file: File): Promise<File> {
    console.log('Начало конвертации HEIC в JPEG');
    try {
        // Динамически импортируем heic-to только когда он нужен
        const { heicTo } = await import('heic-to');
        
        // Создаем новый Blob с явным указанием MIME типа
        const arrayBuffer = await file.arrayBuffer();
        const heicBlob = new Blob([arrayBuffer], { type: 'image/heic' });
        
        console.log('Попытка конвертации HEIC файла через heic-to');
        const jpegBlob = await heicTo({
            blob: heicBlob,
            type: "image/jpeg",
            quality: 0.3
        });

        if (!jpegBlob) {
            throw new Error('Не удалось сконвертировать HEIC файл: результат конвертации пустой');
        }

        const convertedFile = new File([jpegBlob], file.name.replace(/\.heic$/i, '.jpg'), {
            type: 'image/jpeg',
            lastModified: file.lastModified
        });

        console.log('HEIC успешно конвертирован в JPEG:', {
            originalSize: formatBytes(file.size),
            convertedSize: formatBytes(convertedFile.size)
        });

        return convertedFile;
    } catch (error) {
        console.error('Ошибка при конвертации HEIC:', error);
        
        let errorMessage = 'Не удалось сконвертировать HEIC формат.';
        const browserInfo = getBrowserInfo();
        
        if (error instanceof Error && error.message?.includes('format not supported')) {
            errorMessage = 'К сожалению, этот формат HEIC файла не поддерживается в вашем браузере. ';
            errorMessage += 'Пожалуйста, воспользуйтесь одним из следующих способов:\n\n';
            
            if (browserInfo.os === 'MacOS') {
                errorMessage += '1. Конвертация на компьютере:\n';
                errorMessage += '   - Откройте фото в приложении "Просмотр"\n';
                errorMessage += '   - Выберите "Файл" -> "Экспортировать..."\n';
                errorMessage += '   - Выберите формат JPEG или PNG\n\n';
                errorMessage += '2. Через онлайн-сервис:\n';
                errorMessage += '   - heic2jpg.com\n';
                errorMessage += '   - cloudconvert.com';
            } else if (browserInfo.os === 'Windows') {
                errorMessage += '1. Конвертация на компьютере:\n';
                errorMessage += '   - Откройте фото в приложении "Фотографии"\n';
                errorMessage += '   - Нажмите кнопку "..." -> "Сохранить как..."\n';
                errorMessage += '   - Выберите формат JPEG или PNG\n\n';
                errorMessage += '2. Через онлайн-сервис:\n';
                errorMessage += '   - heic2jpg.com\n';
                errorMessage += '   - cloudconvert.com';
            } else if (browserInfo.os === 'iOS') {
                errorMessage += '1. Конвертация на устройстве:\n';
                errorMessage += '   - Откройте фото в приложении "Фото"\n';
                errorMessage += '   - Нажмите кнопку "Поделиться"\n';
                errorMessage += '   - Выберите "Дублировать" или "Сохранить как JPEG"\n\n';
                errorMessage += '2. Альтернативный способ:\n';
                errorMessage += '   - Сделайте скриншот фото\n';
                errorMessage += '   - Загрузите получившийся скриншот';
            } else {
                errorMessage += '1. Через онлайн-сервисы:\n';
                errorMessage += '   - heic2jpg.com\n';
                errorMessage += '   - cloudconvert.com\n\n';
                errorMessage += '2. Сделайте скриншот фото и загрузите его';
            }
        } else if (error instanceof Error && error.message?.includes('not enough memory')) {
            errorMessage = 'Недостаточно памяти для конвертации HEIC файла. Попробуйте:\n';
            errorMessage += '1. Уменьшить размер фото\n';
            errorMessage += '2. Использовать другой браузер\n';
            errorMessage += '3. Сконвертировать файл на компьютере и загрузить уже в формате JPG/PNG';
        }
        
        // Добавляем технические детали для отладки
        const technicalDetails = {
            originalMessage: error instanceof Error ? error.message : String(error),
            fileInfo: {
                size: formatBytes(file.size),
                type: file.type,
                name: file.name
            }
        };
        
        // Логируем ошибку для аналитики
        trackGoal("heic_conversion_error", {
            error_details: technicalDetails,
            browser_info: browserInfo
        });
        
        throw new Error(errorMessage);
    }
}

// Функция для конвертации видео в изображение
export async function convertVideoToImage(file: File): Promise<File> {
    console.log('Начало конвертации видео в изображение');
    try {
        // Создаем промис с таймаутом
        const timeoutPromise = new Promise<File>((_, reject) => {
            setTimeout(() => {
                reject(new Error('Превышено время ожидания обработки видео'));
            }, 30000); // 30 секунд таймаут
        });

        // Создаем промис для обработки видео
        const processPromise = (async () => {
            // Динамически импортируем ffmpeg.js только когда он нужен
            const { FFmpeg } = await import('@ffmpeg/ffmpeg');
            const ffmpeg = new FFmpeg();
            
            // Загружаем ffmpeg
            await ffmpeg.load();
            
            // Записываем видео файл в память ffmpeg
            const fileData = new Uint8Array(await file.arrayBuffer());
            await ffmpeg.writeFile('input.mp4', fileData);
            
            // Извлекаем первый кадр с ограничением размера
            await ffmpeg.exec([
                '-i', 'input.mp4',
                '-vframes', '1',
                '-vf', 'scale=1280:-1', // Ограничиваем ширину до 1280px
                '-f', 'image2',
                'output.jpg'
            ]);
            
            // Читаем результат
            const data = await ffmpeg.readFile('output.jpg');
            
            // Создаем новый файл
            const convertedFile = new File([data], file.name.replace(/\.[^/.]+$/, '.jpg'), {
                type: 'image/jpeg',
                lastModified: file.lastModified
            });

            console.log('Видео успешно конвертировано в изображение:', {
                originalSize: formatBytes(file.size),
                convertedSize: formatBytes(convertedFile.size)
            });

            return convertedFile;
        })();

        // Запускаем обработку с таймаутом
        return await Promise.race([processPromise, timeoutPromise]);

    } catch (error) {
        console.error('Ошибка при конвертации видео:', error);
        
        let errorMessage = 'Не удалось обработать видео.';
        const browserInfo = getBrowserInfo();
        
        if (error instanceof Error) {
            if (error.message.includes('Превышено время ожидания')) {
                errorMessage = 'Обработка видео заняла слишком много времени. Пожалуйста:\n';
                errorMessage += '1. Сделайте скриншот нужного кадра видео\n';
                errorMessage += '2. Или загрузите фото напрямую\n';
                errorMessage += '3. Или попробуйте загрузить видео меньшего размера';
            } else if (error.message.includes('memory')) {
                errorMessage = 'Недостаточно памяти для обработки видео. Попробуйте:\n';
                errorMessage += '1. Сделать скриншот нужного кадра видео\n';
                errorMessage += '2. Или загрузить фото напрямую\n';
                errorMessage += '3. Или попробуйте загрузить видео меньшего размера';
            } else {
                errorMessage = 'Ошибка при обработке видео. Пожалуйста:\n';
                errorMessage += '1. Сделайте скриншот нужного кадра видео\n';
                errorMessage += '2. Или загрузите фото напрямую\n';
                errorMessage += '3. Или попробуйте загрузить видео меньшего размера';
            }
        }
        
        throw new Error(errorMessage);
    }
}

// Функция для проверки и обработки файла
export async function processFile(file: File): Promise<File> {
    const startTime = Date.now();
    const fileInfo = {
        name: file.name,
        type: file.type,
        size: formatBytes(file.size),
        lastModified: new Date(file.lastModified).toISOString()
    };

    console.log('Исходный файл:', fileInfo);

    // Список разрешенных MIME-типов
    const allowedTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/heic',
        'application/pdf',
        'video/mp4',
        'video/quicktime',
        'video/x-msvideo',
        'video/x-matroska'
    ];

    // Проверяем расширение файла для HEIC (так как MIME тип может быть неверным)
    const isHeicFile = file.name.toLowerCase().endsWith('.heic');
    const isVideoFile = file.type.startsWith('video/') || 
                       file.name.toLowerCase().match(/\.(mp4|mov|avi|mkv)$/);
    
    if (!allowedTypes.includes(file.type) && !isHeicFile && !isVideoFile) {
        const error = new Error(
            'Неверный формат файла. Пожалуйста:\n' +
            '1. Загрузите изображение (JPG, PNG, GIF)\n' +
            '2. Или сделайте скриншот документа/фото'
        );
        
        // Логируем ошибку обработки фото
        logPhotoProcessingEvent({
            eventType: PhotoProcessingEventType.PHOTO_PROCESSING_ERROR,
            fileInfo: {
                name: file.name,
                type: file.type,
                size: formatBytes(file.size),
                lastModified: new Date(file.lastModified).toISOString()
            },
            browserInfo: getBrowserInfo(),
            processingTime: Date.now() - startTime,
            errorType: 'invalid_format',
            errorMessage: error.message
        });
        
        trackGoal("take_photo_error", {
            photo_error: error.message,
            browser_info: getBrowserInfo(),
            file_info: fileInfo
        });
        throw error;
    }

    let processedFile = file;
    let success = true;
    let errorMessage = '';

    // Функция для создания таймаута
    const timeout = (ms: number) => new Promise((_, reject) => {
        setTimeout(() => {
            reject(new Error('Превышено время ожидания обработки фото'));
        }, ms);
    });

    // Функция для обработки файла
    const processFileWithTimeout = async (): Promise<File> => {
        if (isVideoFile) {
            processedFile = await convertVideoToImage(file);
        } else if (file.type === 'application/pdf') {
            try {
                processedFile = await convertPdfToImage(file);
            } catch (pdfError) {
                console.error('Ошибка конвертации PDF:', pdfError);
                
                // Логируем ошибку обработки PDF
                logPhotoProcessingEvent({
                    eventType: PhotoProcessingEventType.PHOTO_PROCESSING_ERROR,
                    fileInfo: {
                        name: file.name,
                        type: file.type,
                        size: formatBytes(file.size),
                        lastModified: new Date(file.lastModified).toISOString()
                    },
                    browserInfo: getBrowserInfo(),
                    processingTime: Date.now() - startTime,
                    errorType: 'pdf_conversion_error',
                    errorMessage: pdfError instanceof Error ? pdfError.message : String(pdfError)
                });
                
                // Отправляем метрику
                trackGoal("pdf_conversion_error", {
                    error_message: pdfError instanceof Error ? pdfError.message : String(pdfError),
                    browser_info: getBrowserInfo(),
                    file_info: fileInfo
                });
                
                // Пробрасываем ошибку дальше
                throw pdfError;
            }
        } else if (isHeicFile || file.type === 'image/heic') {
            processedFile = await convertHeicToJpeg(file);
        }
        return processedFile;
    };

    try {
        // Запускаем обработку с таймаутом в 60 секунд
        processedFile = await Promise.race<File>([
            processFileWithTimeout(),
            timeout(60000).then(() => { throw new Error('Превышено время ожидания обработки фото'); })
        ]);
    } catch (conversionError: unknown) {
        console.error('Ошибка при обработке файла:', conversionError);
        success = false;
        errorMessage = conversionError instanceof Error ? conversionError.message : String(conversionError);
        
        // Определяем тип ошибки для логирования
        let errorType = 'conversion_failed';
        if (errorMessage.includes('Превышено время ожидания')) {
            errorType = 'timeout';
            errorMessage = 'Не удалось подготовить фотографию за отведенное время. Пожалуйста:\n' +
                          '1. Попробуйте загрузить другое фото\n' +
                          '2. Или сделайте скриншот этого фото\n' +
                          '3. Или уменьшите размер фото перед загрузкой';
        }
        
        // Логируем ошибку обработки
        logPhotoProcessingEvent({
            eventType: PhotoProcessingEventType.PHOTO_PROCESSING_ERROR,
            fileInfo: {
                name: file.name,
                type: file.type,
                size: formatBytes(file.size),
                lastModified: new Date(file.lastModified).toISOString()
            },
            browserInfo: getBrowserInfo(),
            processingTime: Date.now() - startTime,
            errorType: errorType,
            errorMessage: errorMessage
        });
        
        throw new Error(errorMessage);
    }

    // Логируем успешную обработку фото
    if (success) {
        logPhotoProcessingEvent({
            eventType: PhotoProcessingEventType.PHOTO_PROCESSING_SUCCESS,
            fileInfo: {
                name: file.name,
                type: file.type,
                size: formatBytes(file.size),
                lastModified: new Date(file.lastModified).toISOString()
            },
            resultInfo: {
                size: formatBytes(processedFile.size),
                type: processedFile.type,
                name: processedFile.name
            },
            processingTime: Date.now() - startTime,
            browserInfo: getBrowserInfo()
        });
    }

    return processedFile;
}

// Функция для создания превью PDF
export async function createPdfPreview(file: File): Promise<string> {
    try {
        console.log('Создание превью PDF файла');
        
        // Конвертируем PDF в изображение для превью
        const convertedFile = await convertPdfToImage(file);
        
        // Создаем превью с меньшим размером
        const previewOptions = {
            maxSizeMB: 0.1,
            maxWidthOrHeight: 300,
            useWebWorker: true,
            initialQuality: 0.6,
            fileType: 'image/jpeg'
        };

        const previewFile = await imageCompression(convertedFile, previewOptions);
        
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            
            reader.onload = () => {
                const result = reader.result as string;
                const previewHtml = `<div style='max-width: 100%; margin-bottom: 15px; page-break-inside: avoid; cursor: pointer;'>
                    <img style='max-width: 100%; max-height: 200px; width: auto; height: auto; margin-left: auto; margin-right: auto; display: block; page-break-inside: avoid; border-radius: 10px; border: 1px solid #EAECF3;' 
                         src='${result}' alt="${file.name}" title="Нажмите для просмотра PDF"/>
                </div>`;
                resolve(previewHtml);
            };

            reader.onerror = reject;
            reader.readAsDataURL(previewFile);
        });
    } catch (error) {
        console.error('Ошибка при создании превью PDF:', error);
        // Возвращаем заглушку если не удалось создать превью
        return `<div style='max-width: 100%; margin-bottom: 15px; page-break-inside: avoid; cursor: pointer; border: 1px solid #EAECF3; border-radius: 10px; padding: 20px; text-align: center; background: #f5f5f5;'>
            <div style='font-size: 48px; color: #E22631; margin-bottom: 10px;'>📄</div>
            <div style='font-size: 14px; color: #666;'>${file.name}</div>
            <div style='font-size: 12px; color: #999; margin-top: 5px;'>PDF документ</div>
        </div>`;
    }
}

/** Параметры сжатия как при подписании (getBase64) — два снимка должны влезать в лимит API. */
const IMAGE_COMPRESS_OPTIONS = {
    maxSizeMB: 0.3,
    maxWidthOrHeight: 1024,
    useWebWorker: true,
    initialQuality: 0.7,
    fileType: "image/jpeg" as const,
};

// Функция для конвертации файла в base64
export async function getBase64(
    file: File, 
    maxWidthOrHeight: number = 1024, 
    setImageStringTag: boolean = true,
    addWatermark: boolean = true
): Promise<string> {
    const startTime = Date.now();
    console.log('addWatermark', addWatermark);
    try {
        console.log('Начало сжатия файла:', {
            name: file.name,
            type: file.type,
            size: formatBytes(file.size),
            maxWidthOrHeight: maxWidthOrHeight
        });

        const compressOptions = {
            ...IMAGE_COMPRESS_OPTIONS,
            maxWidthOrHeight: maxWidthOrHeight,
        };

        console.log('Параметры сжатия:', compressOptions);

        const compressedFile = await imageCompression(file, compressOptions).catch(error => {
            console.error('Детальная ошибка при сжатии файла:', {
                error: error,
                errorName: error?.name,
                errorMessage: error?.message,
                errorStack: error?.stack,
                fileInfo: {
                    name: file.name,
                    type: file.type,
                    size: formatBytes(file.size),
                    lastModified: new Date(file.lastModified).toISOString()
                },
                compressionParams: compressOptions,
                browserInfo: getBrowserInfo(),
                memory: window.performance && 'memory' in window.performance ? {
                    jsHeapSizeLimit: formatBytes((performance as any).memory.jsHeapSizeLimit),
                    totalJSHeapSize: formatBytes((performance as any).memory.totalJSHeapSize),
                    usedJSHeapSize: formatBytes((performance as any).memory.usedJSHeapSize)
                } : 'memory info not available'
            });

            // Отправляем информацию об ошибке в систему аналитики
            trackGoal("image_compression_error", {
                error_type: error?.name || 'Unknown',
                error_message: error?.message || 'Undefined error',
                file_type: file.type,
                file_size: formatBytes(file.size),
                browser_info: getBrowserInfo()
            });

            // Логируем ошибку на бэкенд для дальнейшего анализа
            logPhotoProcessingEvent({
                eventType: PhotoProcessingEventType.IMAGE_COMPRESSION_ERROR,
                fileInfo: {
                    name: file.name,
                    type: file.type,
                    size: formatBytes(file.size),
                    lastModified: new Date(file.lastModified).toISOString()
                },
                compressionParams: compressOptions,
                browserInfo: getBrowserInfo(),
                processingTime: Date.now() - startTime
            });

            throw new Error(
                'Не удалось обработать изображение. Пожалуйста:\n' +
                '1. Попробуйте загрузить другое изображение\n' +
                '2. Или сделайте скриншот этого изображения\n' +
                '3. Или уменьшите размер изображения'
            );
        });
        
        console.log('Файл сжат успешно:', {
            originalSize: formatBytes(file.size),
            compressedSize: formatBytes(compressedFile.size),
            compressionRatio: (compressedFile.size / file.size * 100).toFixed(2) + '%',
            finalType: compressedFile.type
        });

        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            
            reader.onload = async () => {
                try {
                    const result = reader.result as string;
                    console.log('Файл прочитан успешно');
                    
                    if (result.startsWith('data:image')) {
                        let finalImage = result;
                        if (addWatermark !== false) {
                            finalImage = await addWatermarkToPhoto(result).catch(error => {
                                console.error('Ошибка при добавлении водяного знака:', error);
                                throw new Error(`Ошибка при добавлении водяного знака: ${error.message}`);
                            });
                            console.log('Водяной знак добавлен');
                        }
                        
                        const element = wrapPhotoElement(finalImage, setImageStringTag);

                        resolve(element);
                    } else {
                        throw new Error('Некорректный формат результата');
                    }
                } catch (error) {
                    console.error('Ошибка при обработке результата:', error);
                    reject(error);
                }
            };

            reader.onerror = (error) => {
                console.error('Ошибка чтения файла:', error);
                reject(error);
            };

            reader.readAsDataURL(compressedFile);
        });
    } catch (error) {
        console.error('Ошибка при сжатии или обработке:', error);
        throw error;
    }
}

/** Сжатие как при подписании, без водяного знака — только raw base64 (без префикса data URL). Для Beorg. */
export async function getVerificationBase64Raw(processedFile: File): Promise<string> {
    const compressedFile = await imageCompression(processedFile, IMAGE_COMPRESS_OPTIONS);
    return new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            if (typeof result === "string") {
                const lowered = result.toLowerCase();
                const marker = "base64,";
                const idx = lowered.indexOf(marker);
                resolve(idx >= 0 ? result.slice(idx + marker.length) : "");
                return;
            }
            reject(new Error("Не удалось прочитать изображение для проверки"));
        };
        reader.onerror = () => reject(new Error("Ошибка чтения файла"));
        reader.readAsDataURL(compressedFile);
    });
} 